<template>
  <section class="filter-view">
    <mc-expandable-section 
      title="Views"
      :title-icon="isViewModeActive ? 'icon-layer-group' : 'icon-layer-group-outline'"
      state-icon-place="right"
      clickable-title
      :expanded="isViewListOpen"
      @expand="isViewListOpen = true"
      @collapse="isViewListOpen = false"
    >
      <mc-loading-icon v-if="isViewsLoading" class="loading-notification"></mc-loading-icon>
      <mc-error v-else-if="isViewsLoadingError" small class="error-notification"></mc-error>
    
      <div v-else-if="!views?.length && !folders?.length" class="no-items-message">
        No Views. Apply filters, then press 'Save' to create your first View.
      </div>

      <div v-else class="filter-view-items">
        <div class="search-input-wrapper">
          <mc-search-input :input-value="searchCriteria" placeholder="Search" @input="handleSearchInputValueChange($event)" />
        </div>

        <div class="scroll-area">
          <div v-if="views.length && !viewsWithAppliedSearch.length" class="no-search-results-message">
            No results found for <span class="search-criteria">“{{ searchCriteria }}”</span>
          </div>

          <filter-view-item v-for="view in viewsWithAppliedSearch.filter(item => item.isDefault)" :key="view.id"
            :view="view"
            :is-selected="isViewModeActive && selectedView?.id === view.id"
            @click:apply="applyView"
            @click:menu-item="handleViewItemMenuItemClick"/>

          <filter-view-folder ref="viewFolders" v-for="folder in folders" :key="folder.id"
            :folder="folder"
            :views="viewsWithAppliedSearch.filter(view => view.folderId === folder.id)"
            :selected-view-id="selectedView?.id"
            @click:menu-item="handleViewItemMenuItemClick"
            @click:view:apply="applyView"
            @click:view:menu-item="handleViewItemMenuItemClick"/>

          <div :class="[
            'views-without-folder',
            { 'dragover': isCursorOverDraggableTarget}]"
            @drop.prevent="handleViewDrop"
            @dragover.prevent="handleDraggableTargetMouseMove(true)"
            @dragleave.prevent="handleDraggableTargetMouseMove(false)"
            @dragenter.prevent
            @drag.prevent
          >
            <filter-view-item v-for="view in viewsWithAppliedSearch.filter(item => !item.folderId && !item.isDefault)" :key="view.id"
              :view="view"
              :is-selected="isViewModeActive && selectedView?.id === view.id"
              draggable
              @click:apply="applyView"
              @click:menu-item="handleViewItemMenuItemClick"/>
          </div>
        </div>
        <div class="folder-create-button-wrapper">
          <filter-button-add title="New Folder" tooltipText="Create new folder" tooltipPlacement="right" :use-default-css-styles="false"
            @click="showSaveDialog(undefined, FilterViewItemType.folder, FilterViewDialogMode.create)"/>
        </div>
      </div> 
    </mc-expandable-section> 

    <mc-confirmation-dialog ref="deleteConfirmationDialog"
      class="confirmation-dialog"
      submitButtonText="Delete for All Users"
      cancelButtonText="Keep"
      :message="itemActionConfig?.confirmationMessage"
      @click:continue="removeViewItem"
      @click:cancel="setItemActionConfigToDefaultState">
      <template #title>
        Delete {{itemActionConfig?.itemType}} <span class="confirmation-dialog-item-name">“{{itemActionConfig?.item?.name}}”</span>?
      </template>
    </mc-confirmation-dialog>

    <filter-view-save-dialog ref="viewSaveDialog" 
      :filter-state="itemActionConfig?.item"
      :dialog-mode="itemActionConfig?.dialogMode"
      :is-objections-feature-enabled="isObjectionsFeatureEnabled" 
      @cancel="setItemActionConfigToDefaultState"/>

    <filter-view-folder-save-dialog ref="folderSaveDialog" 
      :folder="itemActionConfig?.item"
      :dialog-mode="itemActionConfig?.dialogMode"
      @cancel="setItemActionConfigToDefaultState"/>
  </section>
</template>

<script lang="ts" setup>
import { computed, onMounted, ref, nextTick, watch } from 'vue';
import { useRouter } from 'nuxt/app';
import _ from 'lodash';
import McConfirmationDialog from '@/components/ui/McDialog/McConfirmationDialog.vue';
import McError from '@/components/ui/McError.vue';
import McLoadingIcon from '@/components/ui/McLoadingIcon.vue';
import McSearchInput from '@/components/ui/McSearchInput.vue';
import McExpandableSection from "@/components/ui/McExpandableSection.vue";
import FilterViewItem from '@/components/shared/filters/FilterView/FilterViewItem.vue';
import FilterViewFolder from '@/components/shared/filters/FilterView/FilterViewFolder.vue';
import FilterViewSaveDialog from '@/components/shared/filters/FilterDialog/FilterViewSaveDialog.vue';
import FilterViewFolderSaveDialog from '@/components/shared/filters/FilterDialog/FilterViewFolderSaveDialog.vue';
import FilterButtonAdd from '@/components/shared/filters/FilterButton/FilterButtonAdd.vue';
import { useQuickFilterStore } from '@/store/quickFilterStore';
import { useToastStore } from '@/store/toastStore';
import { useUserProfileStore } from '@/store/userProfileStore';
import { quickFilterDataService } from '@/services/QuickFilterApi';
import { appLogger } from '@/services/AppLogger';
import { AppRoutePaths, ROUTES_CONFIG } from '@/constants/appRoutes';
import { DEFAULT_FILTER_STATE } from '@/constants/filter';
import { getViewFolderHtmlElementId, getViewHtmlElementId } from '@/helpers/filterView';
import { 
  FilterViewItemType, 
  FilterViewItemActionType,
  FilterViewDialogMode, 
  IFilterViewItemActionConfig, 
  IFilterViewMenuItemEvent, 
  IQuickFilterFolderViewModel, 
  IQuickFilterOptions, 
  IQuickFilterResponse, 
  IQuickFilterViewModel } from '@/models/quickFilter.model';
import { FeatureName } from '@/models/userProfile.model';
import { UserActions } from '@/models/appLogger.model';

const router = useRouter();
const quickFilterStore = useQuickFilterStore();
const userProfileStore = useUserProfileStore();
const toastStore = useToastStore();

const searchCriteria = ref("");
const folders = computed(() => quickFilterStore.quickFilterFolders);
const views = computed(() => quickFilterStore.quickFilters);
const viewsWithAppliedSearch = computed(() => views.value.filter(filter => filter.name.toLowerCase().includes(searchCriteria.value.toLowerCase())));
const selectedView = computed(() => quickFilterStore.selectedQuickFilter);
const isViewsLoading = computed(() => quickFilterStore.isLoading);
const isViewsLoadingError = computed(() => quickFilterStore.isLoadingError); 
const isObjectionsFeatureEnabled = computed(() => userProfileStore.hasFeature(FeatureName.objectionsTab));

const isViewModeActive = computed(() => {
  const currentRouteName = router.currentRoute.value.name;
  const routeConfig = ROUTES_CONFIG.find((route) => route.name === currentRouteName);
  return routeConfig && routeConfig.isViewMode;
});

const isViewListOpen = ref(true);
const isCursorOverDraggableTarget = ref(false); 

const viewSaveDialog = ref();
const viewFolders = ref();
const folderSaveDialog = ref();
const deleteConfirmationDialog = ref();
const itemActionConfig = ref<IFilterViewItemActionConfig | undefined>(undefined);

const handleSearchInputValueChange = (newValue: string) => {
  searchCriteria.value = newValue;
};

const handleViewDrop = (event: DragEvent) => {
  isCursorOverDraggableTarget.value = false;

  const data = event?.dataTransfer?.getData('text/plain');
  if (!data) return;

  const view = JSON.parse(data) as IQuickFilterViewModel;
  if (!view || !view.folderId) return;

  view.folderId = undefined;

  moveView(view);
};

const handleDraggableTargetMouseMove = (isCursorOver: boolean) => {
  isCursorOverDraggableTarget.value = isCursorOver;
};

const applyView = (view: IQuickFilterViewModel) => {
  if (!view || !view.filter || !view.chips) return;

  const viewId = view.isDefault ? undefined : view.id;
  
  const path = isViewModeActive.value ? router.currentRoute.value.path : AppRoutePaths.overview;
  const query = { viewId };

  router.push({ path, query });
};

const handleViewItemMenuItemClick = (event: IFilterViewMenuItemEvent) => {
  if (!event) return;
  switch (event.actionType) {
    case FilterViewItemActionType.collapseAll:
      collapseAllFolders();
      break;
    case FilterViewItemActionType.copy:
      showSaveDialog(event.data as IQuickFilterViewModel, event.actionItemType, FilterViewDialogMode.copy);
      break;
    case FilterViewItemActionType.openInNewTab:
      openViewInNewTab(event.data as IQuickFilterViewModel);
      break;
    case FilterViewItemActionType.rename:
      showSaveDialog(event.data, event.actionItemType, FilterViewDialogMode.edit);
      break;
    case FilterViewItemActionType.remove:
      showDeleteConfirmationDialog(event.data, event.actionItemType);
      break;
    case FilterViewItemActionType.removeFromFolder:
    case FilterViewItemActionType.move:
      moveView(event.data as IQuickFilterViewModel);
      break;
  }
};

const collapseAllFolders = () => {
  if (!viewFolders.value) return;
  viewFolders.value.forEach((folder: any) => folder.collapse());
};

const openViewInNewTab = (view: IQuickFilterViewModel) => {
  if (!view || !view.id) return;

  const url = router.resolve({
    path: isViewModeActive.value ? router.currentRoute.value.path : AppRoutePaths.overview,
    query: { viewId: view.isDefault ? undefined : view.id },
  }).href;

  window.open(url, '_blank');
};

const showSaveDialog = (
  item: IQuickFilterViewModel | IQuickFilterFolderViewModel | undefined,
  itemType: FilterViewItemType,
  dialogMode: FilterViewDialogMode
) => {
  itemActionConfig.value = { 
    item: item, 
    itemType: itemType,
    dialogMode: dialogMode,
  };

  itemType == FilterViewItemType.view
    ? nextTick(() => viewSaveDialog.value?.openDialog())
    : nextTick(() => folderSaveDialog.value?.openDialog());
};

const showDeleteConfirmationDialog = (
  item: IQuickFilterViewModel | IQuickFilterFolderViewModel, 
  itemType: FilterViewItemType
) => {
  itemActionConfig.value = { 
    item: item, 
    itemType: itemType,
    confirmationMessage: itemType === FilterViewItemType.view
      ? "This will permanently remove the View for all users in the system."
      : "This will permanently remove the Folder for all users in the system. Any views inside won't be deleted — they`ll be moved to the main list."
  };

  nextTick(() => deleteConfirmationDialog.value?.openDialog());
};

const moveView = (changedView: IQuickFilterViewModel) => {
  if (!changedView) return;

  let destinationFolder = undefined;

  if (changedView.folderId) {
    destinationFolder = folders.value?.find(folder => folder.id === changedView.folderId);
    if (!destinationFolder) {
      return;
    }
  }

  const requestOptions = {
    id: changedView.id,
    name: changedView.name,
    folderId: changedView.folderId,
    data: changedView.filter,
  } as IQuickFilterOptions;

  appLogger.logUserAction(
    UserActions.viewFilterMove, 
    { 
      viewId: requestOptions.id ?? '',
      view: requestOptions.name, 
      folderId: requestOptions.folderId ?? 'root' 
    });

  quickFilterDataService.upsertAsync(requestOptions)
    .then((response: IQuickFilterResponse) => {
      changedView.id = response.id;
      changedView.name = response.name;
      quickFilterStore.updateQuickFilter(changedView);

      destinationFolder
        ? toastStore.showItemSuccessfullyMovedToFolder(changedView.name, destinationFolder.name)
        : toastStore.showItemSuccessfullyRemovedFromFolder(changedView.name);

    })
    .catch((error: any) => {
      if (error.response?.status === 404) {
        if (destinationFolder) {
          toastStore.showItemNoLongerExists(destinationFolder.name);
          quickFilterStore.removeQuickFilterFolder(destinationFolder.id);
        }
      }
      else {
        throw error;
      }
    })
    .finally(() => {
      setItemActionConfigToDefaultState();
    });
};

const removeViewItem = () => {
  if (!itemActionConfig.value || !itemActionConfig.value.item) return;

  const itemName = itemActionConfig.value?.item.name;
  const itemId = itemActionConfig.value.item.id;
  const itemType = itemActionConfig.value.itemType;

  const removeItemPromise = itemType === FilterViewItemType.view
    ? quickFilterDataService.deleteAsync(itemId)
    : quickFilterDataService.deleteFolderAsync(itemId);

  appLogger.logUserAction(
    itemType === FilterViewItemType.view
      ? UserActions.viewFilterDelete
      : UserActions.viewFolderDelete, 
    { 
      itemId: itemId ?? '',
      itemName: itemName, 
    });

  removeItemPromise
    .then(() => toastStore.showItemSuccessfullyDeleted(itemName))
    .catch((error: any) => {
      if (error.response?.status === 404) {
        toastStore.showItemNoLongerExists(itemName);
      }
      else {
        throw error;
      }
    })
    .finally(() => {
      if (itemType === FilterViewItemType.view) {
        const selectedViewId = selectedView.value.id;
        quickFilterStore.removeQuickFilter(itemId);
        if (selectedViewId === itemId) applyView(DEFAULT_FILTER_STATE);
      }
      else if (itemType === FilterViewItemType.folder) {
        quickFilterStore.removeQuickFilterFolder(itemId);
      }

      setItemActionConfigToDefaultState();
    });
};

const setItemActionConfigToDefaultState = () => {
  itemActionConfig.value = undefined;
};

const scrollToView = (viewId: string | undefined) => {
  const view = views.value?.find((view) => view.id === viewId);
  if (!view) return;

  // Expand the folder if the specified view is contained in it
  if (view.folderId) {
    viewFolders.value?.find((folder: any) => folder.folderHtmlElementId === getViewFolderHtmlElementId(view.folderId))?.expand();
  }

  nextTick(() => {
    const viewHtmlElementId = getViewHtmlElementId(view.id);
    const selectedViewElement = document.getElementById(viewHtmlElementId);
    if (selectedViewElement) {
      selectedViewElement.scrollIntoView({ block: 'nearest' });
    }
  });
};

defineExpose({
  isViewListOpen,
  isViewModeActive
});

onMounted(async () => {
  await quickFilterStore.setQuickFilterItemsAsync();
  nextTick(() => scrollToView(selectedView.value.id));
});

watch(
  () => selectedView.value,
  () => nextTick(() => scrollToView(selectedView.value.id)),
  { deep: true }
);
</script>

<style lang="scss" scoped>
.filter-view {
  --expandable-section-header-height: 40px;
  --expandable-section-header-padding: 0;
  --expandable-section-height: 100%;
  --expandable-section-state-icon-wrapper-size: 24px;
  --expandable-section-state-icon-wrapper-background-color: var(--colour-background-neutral-default-a-dark);
  --expandable-section-state-icon-wrapper-border-radius: 3px;
  --expandable-section-state-icon-font-size: 26px;
  --expandable-section-state-icon-font-weight: var(--font-weight-body-regular);
  --expandable-section-state-icon-color: var(--colour-icons-neutral-default-dark);
  --expandable-section-title-icon-color: var(--colour-icons-neutral-default-dark);
  --expandable-section-title-icon-font-size: 14px;
  --expandable-section-title-icon-font-weight: var(--font-weight-body-regular);
  --expandable-section-title-icon-padding: 0 14px 0 4px;
  --expandable-section-title-font-color: var(--colour-text-neutral-default-dark);
  --expandable-section-title-font-size: var(--font-size-headers-x-small);
  --expandable-section-title-font-weight: var(--font-weight-heading-regular);
  --expandable-section-content-height: calc(100% - var(--expandable-section-header-height));
  --expandable-section-content-padding: 4px 0;

  display: flex;
  flex-direction: column;
  gap: 8px;
  justify-content: flex-start;
  height: 100%;
  width: 100%;
  padding: 0;
  overflow: hidden;

  .loading-notification {
    display: flex;
    justify-self: center;
  }

  .error-notification {
    justify-content: flex-start;
    height: auto;
  }

  .no-items-message {
    font-size: var(--font-size-body-regular);
    font-style: normal;
    font-weight: var(--font-weight-body-regular);
    line-height: var(--line-height-s);
    color: var(--colour-text-disabled-dark);
  }

  .filter-view-items {
    display: flex;
    flex-direction: column;
    height: 100%;
    overflow: hidden;
    transition: max-height 0.3s ease-in-out;
  }

  .search-input-wrapper {
    --search-input-text-color: var(--colour-text-neutral-default-dark);
    --search-input-loupe-icon-color: var(--colour-icons-neutral-subtle-dark);
    --search-input-placeholder-color: var(--colour-text-disabled-dark);
    --search-input-cross-icon-color: var(--colour-icons-contrast);
    --search-input-border-color: var(--colour-icons-disabled-dark);
    --search-input-border-color-hover: var(--colour-icons-disabled-dark);
    --search-input-border-color-active: var(--colour-border-brand-default-dark);
    --search-input-border-color-focus: var(--colour-border-brand-default-dark);
    --search-input-ring-width-focus: 0px;

    padding-bottom: 8px;
  }

  .no-search-results-message {
    color: var(--colour-text-neutral-subtle-dark);
    font-size: var(--font-size-body-regular);
    font-weight: var(--font-weight-body-regular);
    line-height: 22px;
    max-width: 100%;
    padding: 10px 5px;

    .search-criteria {
      font-weight: var(--font-weight-body-medium);
      word-wrap: break-word;
    }
  }

  .scroll-area {
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: flex-start;
    align-self: stretch;
    gap: 2px;
    padding: 0 4px 4px 0;
    overflow-y: auto;

    .views-without-folder {
      display: flex;
      flex-direction: column;
      width: 100%;
      gap: 2px;
      border: 1px solid transparent;

      &.dragover {
        border-radius: 8px;
        border: 1px solid var(--colour-icons-disabled-dark);
        background-color: var(--colour-background-neutral-hover-a-dark) !important;
      }
    }
  }

  .folder-create-button-wrapper {
    --filter-button-add-background-color: transparent;
    --filter-button-add-background-hover-color: var(--colour-background-neutral-hover-a-dark);
    --filter-button-add-button-height: 24px;
    --filter-button-add-icon-font-size: 24px;
    --filter-button-add-text-color: var(--colour-text-neutral-subtle-dark);
    --filter-button-add-icon-color: var(--colour-icons-neutral-subtle-dark);

    display: flex;
    padding: 4px 10px;
  }

  .confirmation-dialog {
    --dialog-actions-section-margin: 32px 0 0 0;
  }

  .confirmation-dialog-item-name {
    font-weight: var(--font-weight-body-medium);
    word-wrap: break-word;
    text-wrap: pretty;
  }

  ::-webkit-scrollbar-track {
    background: rgba(79, 0, 68, 0.4); 
  }

  ::-webkit-scrollbar-thumb {
    background: #34002d;
    border-radius: 4px;
  }

  ::-webkit-scrollbar-thumb:hover {
    background: #30002a;
  }
}
</style>